import { Box, Divider, Text } from '@mantine/core';

import { AbstractCard } from '@/module/Author/Abstract/components/Card/AbstractCard';
import { ArticleImpactFactor } from '@/module/Author/Article/components/ArticleImpactFactor';
import { ArticleInfluence } from '@/module/Author/Article/components/ArticleInfluence';
import { ArticleCard } from '@/module/Author/Article/components/Card/ArticleCard';
import { BookCard } from '@/module/Author/Book/components/Card/BookCard';
import { ChapterCard } from '@/module/Author/Chapter/components/Card/ChapterCard';
import { CommitteeCard } from '@/module/Author/Committee/components/Card/CommitteeCard';
import { CourseCard } from '@/module/Author/Course/components/Card/CourseCard';
import { DoctoralCommitteeCard } from '@/module/Author/DoctoralCommittee/components/Card/DoctoralCommitteeCard';
import { EditorialTeamCard } from '@/module/Author/EditorialTeam/components/Card/EditorialTeamCard';
import { PatentCard } from '@/module/Author/Patent/components/Card/PatentCard';
import { ProceedingCard } from '@/module/Author/Proceeding/components/Card/ProceedingCard';
import { ProjectCard } from '@/module/Author/Project/components/Card/ProjectCard';
import { TranslationCard } from '@/module/Author/Translation/components/Card/TranslationCard';
import {
  EvaluationCriteriaDataItem,
  isEvaluationCriteriaAbstractItem,
  isEvaluationCriteriaArticleItem,
  isEvaluationCriteriaBookItem,
  isEvaluationCriteriaBooleanItem,
  isEvaluationCriteriaChapterItem,
  isEvaluationCriteriaCommitteeItem,
  isEvaluationCriteriaCourseItem,
  isEvaluationCriteriaDoctoralCommitteeItem,
  isEvaluationCriteriaEditorialTeamItem,
  isEvaluationCriteriaFreeItem,
  isEvaluationCriteriaNumberItem,
  isEvaluationCriteriaPatentItem,
  isEvaluationCriteriaProceedingItem,
  isEvaluationCriteriaProjectItem,
  isEvaluationCriteriaTranslationItem,
} from '@/module/Evaluation/model/EvaluationTypes';

import { assertUnreachable } from '@feature/utils';

interface EvaluationItemCardHeaderProps {
  item: EvaluationCriteriaDataItem;
  dataEntryHint: string | null;
}

export function EvaluationItemCardHeader({ item, dataEntryHint }: EvaluationItemCardHeaderProps) {
  if (isEvaluationCriteriaFreeItem(item)) {
    return <Text>{item.activityDescription}</Text>;
  }

  if (isEvaluationCriteriaBooleanItem(item)) {
    return <Text fw="500">DA</Text>;
  }

  if (isEvaluationCriteriaNumberItem(item)) {
    return (
      <Text>
        {dataEntryHint}: {item.number}
      </Text>
    );
  }

  if (isEvaluationCriteriaCourseItem(item)) {
    return (
      <CourseCard
        course={item.course}
        onClick={() => window.open(`/author/course/${item.course.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaAbstractItem(item)) {
    return (
      <AbstractCard
        abstract={item.abstractItem}
        onClick={() => window.open(`/author/abstract/${item.abstractItem.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaArticleItem(item)) {
    return (
      <Box flex={1}>
        <ArticleCard
          article={item.article}
          onClick={() => window.open(`/author/article/${item.article.id}`)}
        />
        <Box p="10px">
          <ArticleImpactFactor
            hideTemporary
            articleId={item.article.id}
          />
          <Divider my="10px" />
          <ArticleInfluence
            hideTemporary
            articleId={item.article.id}
          />
        </Box>
      </Box>
    );
  }

  if (isEvaluationCriteriaBookItem(item)) {
    return (
      <BookCard
        book={item.book}
        onClick={() => window.open(`/author/book/${item.book.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaTranslationItem(item)) {
    return (
      <TranslationCard
        translation={item.translation}
        onClick={() => window.open(`/author/translation/${item.translation.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaChapterItem(item)) {
    return (
      <ChapterCard
        chapter={item.chapter}
        onClick={() => window.open(`/author/chapter/${item.chapter.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaProceedingItem(item)) {
    return (
      <ProceedingCard
        proceeding={item.proceeding}
        onClick={() => window.open(`/author/proceeding/${item.proceeding.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaPatentItem(item)) {
    return (
      <PatentCard
        patent={item.patent}
        onClick={() => window.open(`/author/patent/${item.patent.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaProjectItem(item)) {
    return (
      <ProjectCard
        project={item.project}
        onClick={() => window.open(`/author/project/${item.project.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaCommitteeItem(item)) {
    return (
      <CommitteeCard
        committee={item.committee}
        onClick={() => window.open(`/author/committee/${item.committee.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaDoctoralCommitteeItem(item)) {
    return (
      <DoctoralCommitteeCard
        doctoralCommittee={item.committee}
        onClick={() => window.open(`/author/doctoral-committee/${item.committee.id}`)}
      />
    );
  }

  if (isEvaluationCriteriaEditorialTeamItem(item)) {
    return (
      <EditorialTeamCard
        editorialTeam={item.editorialTeam}
        onClick={() => window.open(`/author/editorial-team/${item.editorialTeam.id}`)}
      />
    );
  }

  assertUnreachable(item);
}
